/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import {
    func, object, string, shape, bool,
} from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import mbpLogger from 'mbp-logger';
import { useQuery } from '@apollo/client';
import Dialog from '@material-ui/core/Dialog';
import { Button, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { deepObjectExtend, isEmpty } from '../../../utils/object';
import { main as mainStyle } from '../../../style/main';

import cancelModalIconPUF from '../Images/skipIcon.png';
import cancelModalIcon from '../Images/skipPauseIcon.png';
import CancellationSurvey from './CancellationSurvey';
import findContent from '../../../../../gql/queries/findContent';
import CanceledSubscriptionSuccess from './CanceledSubscriptionSuccess';
import { brandByCode } from '../helper';

const styles = ({ typography, palette }) => deepObjectExtend(mainStyle(typography, palette), {
    dialogRoot: {
        width: '100%',
        boxSizing: 'border-box',
        alignItems: 'normal',
        height: '100%',
        overflowY: 'scroll',
    },
    dialogPaper: {
        margin: '0',
        width: '100%',
        maxWidth: '454px',
        maxHeight: '673px',
        overflowY: 'auto',
        borderRadius: '0',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        boxShadow: '0px 0px 20px 6px rgba(0,0,0,0.5)',
        '@media screen and (min-width: 768px)': {
            maxHeight: '580px',
        },
        '@media screen and (min-width: 400px)': {
            margin: '19.5px',
        },
        '@media screen and (max-height: 400px)': {
            top: 0,
            position: 'absolute',
            marginBottom: '50px',
        },
        '@media screen and (max-height: 600px)': {
            height: '100%',
        },
    },
    closeIconLabel: {
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    cancelCartImg: {
        height: '150px',
        alignSelf: 'center',
        margin: '10px 120px 30px 120px',
    },
    cancelCartImgSmall: {
        width: '134px',
        height: '108px',
        alignSelf: 'center',
        margin: '74px 120px 50px 121px',
    },
    contentContainer: {
        padding: '0 40px',
        fontSize: '20px',
        lineHeight: '28px',
        letterSpacing: '1px',
        textAlign: 'center',
    },
    marginBottom20: {
        marginBottom: '20px',
    },
    secondaryButtonStyle: {
        backgroundColor: 'transparent',
        color: palette.black,
        fontSize: '18px',
        fontWeight: 'bold',
        padding: '12px 16px',
        borderRadius: '30px',
        border: `1px solid ${palette.black}`,
        width: '100%',
        boxShadow: '2px 2px 4px rgba(47, 47, 47, 0.15)',
        marginBottom: '25px',
        '@media screen and (max-width: 400px)': {
            fontSize: '12px',
        },
    },
    primaryButtonStyle: {
        color: palette.white,
        backgroundColor: palette.primaryButton,
        fontSize: '18px',
        fontWeight: 'bold',
        padding: '12px 16px',
        borderRadius: '30px',
        border: `1px solid ${palette.primaryButton}`,
        boxShadow: '2px 2px 4px rgba(47, 47, 47, 0.15)',
        marginBottom: '15px',
        width: '100%',
        '&:hover': {
            backgroundColor: '#80061a',
            color: palette.white,
            borderColor: '#80061a',
        },
        '@media screen and (max-width: 400px)': {
            fontSize: '12px',
        },
    },
    btnContainer: {
        position: 'relative',
    },
    btnSubContainer: {
        marginBottom: '22px',
        padding: '0 20px',
        bottom: 0,
        left: 0,
        width: '100%',
    },
    xContainer: {
        width: '50px',
        position: 'absolute',
        top: '3px',
        right: '0px',
    },
});

function CancelSubscriptionDialog({
    openModal, classes, handleModal, subscription, selectedRuleId, handleCancel, featureFlags,
}) {
    let CANCELLATION_SURVEY_ENABLED = featureFlags['is-cancellation-survey-enabled'];
    const [openCancelSurveyForm, setOpenCancelSurveyForm] = useState(false);
    const [openSuccess, setOpenSuccess] = useState(false);
    const query = '\\"url\\": \\"/subscriptions\\"';
    const contentType = 'subscriptions_management';
    const SUBSCRIPTIONS_MANAGEMENT = findContent({
        brand: brandByCode[subscription.brand] || '1800flowers',
        contentType,
        query,
    });

    const { data, error, loading } = useQuery(SUBSCRIPTIONS_MANAGEMENT);

    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            jsError: error,
            message: `Failed to fetch content data for: content type ${contentType}, query ${query}`,
        });
        CANCELLATION_SURVEY_ENABLED = false;
    }

    const cancellationSurveyData = data?.findContent?.content?.entries?.[0] || {};
    const surveyData = cancellationSurveyData?.cancelation_survey || {};

    if (isEmpty(surveyData) && !loading) CANCELLATION_SURVEY_ENABLED = false;

    const allowCancellationFirst = cancellationSurveyData?.allow_cancellation_first || false;
    const handleCancelBtn = (event, id, button) => {
        if (CANCELLATION_SURVEY_ENABLED) {
            if (allowCancellationFirst) {
                handleCancel('cancellation_survey', id, button);
                setOpenSuccess(true);
            }
            setOpenCancelSurveyForm(true);
        } else handleCancel(event, id, button);
    };

    const closeModal = () => {
        if (openSuccess || openCancelSurveyForm) handleCancel('cancel', selectedRuleId, 'close modal');
        handleModal('', selectedRuleId, 'close');
    };

    return (
        <Dialog
            data-testid="subscription-recipient-form-modal"
            open={openModal}
            onClose={closeModal}
            classes={{
                root: classes.dialogRoot,
                paper: classes.dialogPaper,
            }}
        >
            <div className={classes.xContainer}>
                <IconButton
                    aria-label="delete"
                    align="right"
                    data-test="checkout-recipient-form-close"
                    data-testid="checkout-recipient-form-close"
                    id="recipientFormCloseLink"
                    classes={{
                        label: classes.closeIconLabel,
                    }}
                    onClick={closeModal}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </div>
            <br />
            {openSuccess && <CanceledSubscriptionSuccess subscription={subscription} setOpenSuccess={setOpenSuccess} setOpenCancelSurveyForm={setOpenCancelSurveyForm} handleModal={handleModal} handleCancel={handleCancel} />}
            {!openSuccess && (openCancelSurveyForm ? (
                <CancellationSurvey
                    data={surveyData}
                    handleCancel={handleCancel}
                    subscription={subscription}
                />
            ) : (
                <>
                    {subscription.subscriptionStatus === 'PAUSED' || !featureFlags['sub-cancel-variant'] ? (
                        <>
                            <img src={cancelModalIconPUF} className={classes.cancelCartImgSmall} alt="fireSpot" />
                            <div className={classes.contentContainer}>
                                <div className={classes.marginBottom20}>Are you sure you&apos;d like to CANCEL your subscription?</div>
                                <div className={classes.marginBottom20}>Cancellation can not be UNDONE.</div>
                            </div>
                            <div className={classes.btnContainer}>
                                <div className={classes.btnSubContainer}>
                                    <Button onClick={() => handleCancelBtn('cancel', selectedRuleId, 'Yes')} className={classes.secondaryButtonStyle}>YES</Button>
                                    <Button onClick={() => handleModal('', selectedRuleId, 'No, Keep Subscription')} className={classes.primaryButtonStyle}>NO, KEEP SUBSCRIPTION</Button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <img src={cancelModalIcon} className={classes.cancelCartImg} alt="fireSpot" />
                            <div className={classes.contentContainer}>
                                <div className={classes.marginBottom20}>Are you SURE you want to cancel?</div>
                                <div className={classes.marginBottom20}>You can skip your next delivery and keep your subscription active instead.</div>
                            </div>
                            <div className={classes.btnContainer}>
                                <div className={classes.btnSubContainer}>
                                    <Button onClick={() => handleModal('skip', selectedRuleId, 'Skip next delivery')} className={classes.primaryButtonStyle}>SKIP NEXT DELIVERY</Button>
                                    <Button onClick={() => handleCancelBtn('cancel', selectedRuleId, 'Yes, cancel my subscription')} className={classes.secondaryButtonStyle}>YES, CANCEL MY SUBSCRIPTION</Button>
                                </div>
                            </div>
                        </>
                    )}
                </>
            ))}
        </Dialog>
    );
}

CancelSubscriptionDialog.propTypes = {
    classes: object.isRequired,
    subscription: shape({
        ruleId: string,
    }),
    handleModal: func.isRequired,
    featureFlags: shape({
        'sub-cancel-variant': bool,
    }).isRequired,
    openModal: bool.isRequired,
    handleCancel: func.isRequired,
    selectedRuleId: string.isRequired,
};

CancelSubscriptionDialog.defaultProps = {
    subscription: {},
};

export default withStyles(styles)(CancelSubscriptionDialog);
